import React, { useMemo, useRef } from 'react'

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Link,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import equal from 'fast-deep-equal'
import { Provider, useAtomValue } from 'jotai'
import AutosizeInput from 'react-input-autosize'
import { Navigate, useParams } from 'react-router'
import { colors } from 'src/assets/colors'
import { white } from 'src/components/__legacy__/color/palette'
import { Spacer } from 'src/components/__legacy__/spacer/spacer'
import { HashLink } from 'src/components/HashLink/HashLink'
import { Edit, Followup } from 'src/components/icon'
import { usePermission } from 'src/features/auth/context'
import { useBlockTransition } from 'src/hooks/useBlockTransition'
import { TemplateStatus } from 'src/legacy_modules/entities/template/entity'
import { PERMISSIONS } from 'src/lib/permission'
import styled from 'styled-components'
import { FlexItem } from 'styled-flex-component'

import { templateDetailAtom } from './atom'
import { MediumItemGroup } from './medium-item-group'
import { useTemplateEntities } from '../../template-entities-context'
import { PreviewButton } from './preview/button'
import { PulldownContainer } from './pulldown/pulldown-container'
import { ReleaseButton } from './release/button'
import { SortOnlyMediumItemGroup } from './sort-only-medium-item-group'
import { UpdateTitleButton } from './update-title/button/update-title-button'
import { useEditTemplate } from './use-edit-template'
import { useFetch } from './use-fetch'
import { AddMediumItem } from '../../../common/add-medium-item'
import { trialIdParamName } from '../../../trial-detail'
import { TemplateStatusBadge } from '../status-badge'

export const templateIdParamName = 'templateUid'

type Props = {}

export const TemplateDetail: React.FC<Props> = () => (
  <Provider>
    <TemplateDetailInner />
  </Provider>
)

const TemplateDetailInner: React.FC = () => {
  const { trialUid = '', templateUid = '' } = useParams<{
    [templateIdParamName]: string
    [trialIdParamName]: string
  }>()
  const { getTemplate, getTemplateFolder } = useTemplateEntities()
  const template = getTemplate(templateUid)
  const templateFolder = getTemplateFolder(template?.templateFolderUid ?? '')
  const v2 = true

  const { originalTemplate, title, videoEnabled, mediumItems } =
    useAtomValue(templateDetailAtom)

  // 使用する？？
  // const { errorUidMap } = useHandleErrors({
  //   template,
  //   mediumItems,
  //   v2,
  // })
  const {
    releaseTried,
    handlers: {
      onValidate,
      onAdd,
      onSubmit,
      onSubmitOrder,
      onChangeTitle,
      onChangeVideoEnabled,
    },
  } = useEditTemplate({
    template,
    mediumItems,
    observationType: templateFolder?.observationType,
  })

  const isEdited = useMemo(() => {
    if (!originalTemplate) return false
    if (originalTemplate.title !== title) return true
    if (originalTemplate.videoEnabled !== videoEnabled) return true

    return !equal(originalTemplate.mediumItems, mediumItems)
  }, [mediumItems, originalTemplate, title, videoEnabled])

  const titleRef = useRef<AutosizeInput>(null)
  const focusToTitle = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(titleRef as any).current.focus()
  }

  const { fetched } = useFetch({
    trialUid,
    templateUid,
    pathForNotFound: '../',
  })

  const { hasPermission } = usePermission()

  const isReleased = template?.status === TemplateStatus.Released
  const isDisabled = template?.status === TemplateStatus.Disabled

  // 編集可能であればtrue,編集不可(公開・無効化)であればfalse
  const isEditMode = !isReleased && !isDisabled

  const canSwitchVideoSelect = hasPermission(PERMISSIONS.Template_Edit)
  const showVideoSelect = useMemo(() => {
    if (!template) return false

    return isEditMode && canSwitchVideoSelect && !v2
  }, [template, isEditMode, canSwitchVideoSelect, v2])
  const showVideoTag = !showVideoSelect && !!template?.videoEnabled
  const canEdit = isEditMode && hasPermission(PERMISSIONS.Template_Edit)
  const canEditOrder = canEdit || isReleased // 公開後はDr,CRCも順番を変更できる
  const canSave = isEditMode && hasPermission(PERMISSIONS.Template_Save)
  const canRelease = isEditMode && hasPermission(PERMISSIONS.Template_Release)
  const canRenameTitle =
    isEditMode && hasPermission(PERMISSIONS.Template_Rename)

  const canRenameTitleInDisabled =
    !isEditMode && hasPermission(PERMISSIONS.Template_Rename)

  const GroupComponent = canEdit ? MediumItemGroup : SortOnlyMediumItemGroup

  useBlockTransition({
    showAlert: isEdited,
  })

  if (!fetched && !template) {
    return null
  }
  if (!template || !templateFolder) {
    return <Navigate replace to={'./'} />
  }

  const releaseButtonEnabled =
    template.status === TemplateStatus.Draft && !isEdited
  const saveButtonEnabled = !releaseButtonEnabled

  return (
    <Flex h="full" direction="column" py="5">
      <TopContainer>
        <Wrap spacingX="3" alignItems="center" align="center">
          <WrapItem alignItems="center" gap="3" w="full" overflowX="auto">
            <Flex gap="1.5" align="center">
              {templateFolder.observationType === 'FollowUp' && (
                <Box color="gray.500">
                  <Followup />
                </Box>
              )}
              <Text fontWeight="bold" fontSize="lg" wordBreak="keep-all">
                {templateFolder.name}
              </Text>
            </Flex>
            <Box color="gray.400" fontWeight="bold" fontSize="lg">
              /
            </Box>
            <Flex gap="3" align="center">
              {canRenameTitle && (
                <HStack spacing="2.5">
                  <TitleInput
                    ref={titleRef}
                    value={title}
                    inputStyle={{ fontSize: 18, fontWeight: 'bold' }}
                    onChange={event => onChangeTitle(event.target.value)}
                    className={
                      releaseTried &&
                      (titleRef.current && titleRef.current.props.value) === ''
                        ? 'error'
                        : ''
                    }
                  />
                  <IconButton
                    icon={<Edit />}
                    aria-label="edit template title"
                    onClick={() => focusToTitle()}
                    variant="customIconButtonGhost"
                  />
                </HStack>
              )}

              {!canRenameTitle && (
                <Text fontWeight="bold" fontSize="lg" wordBreak="keep-all">
                  {template.title}
                </Text>
              )}

              {/* ↓v2の場合一時的に非表示にする: https://bubbys.slack.com/archives/C01EBGH4TFT/p1687498074403549 */}
              {!v2 && canRenameTitleInDisabled && (
                <UpdateTitleButton templateUid={template.uid} />
              )}
            </Flex>
          </WrapItem>

          <WrapItem>
            <TemplateStatusBadge status={template.status} />
          </WrapItem>

          {showVideoSelect && (
            <WrapItem>
              <VideoPossibilitySelect>
                <Checkbox
                  isChecked={videoEnabled}
                  onChange={e => onChangeVideoEnabled(e.target.checked)}
                  isDisabled={template.presetUsed}
                >
                  ビデオ通話を使用する
                </Checkbox>
              </VideoPossibilitySelect>
            </WrapItem>
          )}

          {showVideoTag && (
            <WrapItem>
              <Badge colorScheme="green">ビデオ通話あり</Badge>
            </WrapItem>
          )}
        </Wrap>

        <Spacer size={10} />

        <ButtonsContainer>
          <FlexItem>
            <PreviewButton
              template={template}
              templateFolderName={templateFolder.name}
            />
          </FlexItem>

          {canSave && (
            <FlexItem>
              <SaveButton
                onClick={onSubmit}
                disabled={!saveButtonEnabled}
              ></SaveButton>
            </FlexItem>
          )}

          {canRelease && (
            <FlexItem>
              <ReleaseButton
                onValidate={onValidate}
                disabled={!releaseButtonEnabled}
              />
            </FlexItem>
          )}

          {template.status === TemplateStatus.Released && !v2 && (
            <FlexItem>
              <Button variant="outline" onClick={onSubmitOrder}>
                順番を保存
              </Button>
            </FlexItem>
          )}

          <FlexItem>
            <PulldownContainer
              status={template.status}
              templateUid={templateUid}
              v2={v2}
            />
          </FlexItem>
        </ButtonsContainer>
      </TopContainer>

      <Spacer size={32} />

      {fetched && (
        <Box flex="1" overflow="auto" px="4">
          <Wrap
            bg="blue.50"
            px="4"
            py="2"
            spacing="6"
            as="nav"
            aria-label="section navigation"
          >
            {mediumItems.map(mi => (
              <WrapItem key={mi.uid}>
                <HStack pos="relative">
                  <Box
                    as="span"
                    h="20px"
                    w="4px"
                    borderRadius="full"
                    bg="blue.300"
                  />
                  <Link as={HashLink} to={`#${mi.uid}`} color="blue.500">
                    {mi.title}
                  </Link>
                </HStack>
              </WrapItem>
            ))}
          </Wrap>

          <Spacer size={16} />

          <MediumItemsContainer>
            <Stack spacing="5">
              {mediumItems.map((mi, i) => (
                <GroupComponent
                  key={mi.uid}
                  index={i}
                  mediumItem={mi}
                  releaseTried={releaseTried}
                  orderEditable={canEditOrder}
                  templateStatus={template.status}
                  hideRolePermission={
                    templateFolder.observationType !== 'DocumentExchange'
                  }
                />
              ))}
              {canEdit && <AddMediumItem onAdd={onAdd} />}
            </Stack>
          </MediumItemsContainer>
        </Box>
      )}
    </Flex>
  )
}

type SaveButtonProps = {
  onClick: () => void
  disabled: boolean
}
const SaveButton: React.FC<SaveButtonProps> = ({ onClick, disabled }) => {
  return (
    <Button onClick={onClick} variant="outline" isDisabled={disabled}>
      一時保存
    </Button>
  )
}

const TitleInput = styled(AutosizeInput)`
  resize: horizontal;
  max-width: calc(100% - 32px); // 32px = The width of pen-icon

  input {
    max-width: 100%;
    font-family: inherit;
    color: ${colors.dark};
    padding: 2px;
    border: 1px solid;
    outline: none;
    border-color: transparent;
    font-weight: bold;
    background-color: ${white};

    &:hover {
      border-color: rgba(0, 0, 0, 0.2);
    }

    &:focus {
      border-color: rgba(0, 0, 0, 0.2);
    }
  }

  border: none;
  outline: none;

  &.error {
    input {
      background: ${colors.redLighten5};
      border-color: ${colors.redLighten3};
      box-shadow: 0 0 2px ${colors.redDarken4};
      min-width: 35px;
    }
  }
`

const VideoPossibilitySelect = styled.div``

const SCROLL_BAR_WIDTH = 6
const MediumItemsContainer = styled.div`
  flex-grow: 1;
  overflow-y: scroll;
  padding: 0 ${16 - SCROLL_BAR_WIDTH}px 0 0;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;

  > div:not(:last-child) {
    margin-right: 12px;
  }
`

const TopContainer = styled.div`
  padding: 0 16px;
`
